import React from 'react'
import styled from 'styled-components'
import { line, font } from 'features/dashboard/tokens'

/**
 * One ring sliced into N arcs (one per segment / TaskDomain). For each
 * segment, a faded backing arc shows the slot allocated to that domain
 * and a solid arc on top shows the completion percentage within it.
 *
 * Props:
 *   segments: [{ id, name, colour, weight, pct }]
 *   size, stroke
 *   centre: ReactNode rendered absolute-centred inside the ring
 */
const SegmentedDonut = ({ segments = [], size = 96, stroke = 11, centre = null }) => {
  // Detect "no scored data" — either no segments at all or every segment
  // is null/0. Render a dashed outline ring instead of a flat grey loop so
  // the empty state reads as "awaiting data" rather than "0%".
  const hasScore = segments.some(s => s && s.pct !== null && s.pct !== undefined && s.pct > 0)
  if (!segments.length || !hasScore) {
    const r = (size - stroke) / 2
    return (
      <Wrap style={{ width: size, height: size }}>
        <svg width={size} height={size}>
          <circle cx={size / 2} cy={size / 2} r={r}
            fill='none'
            stroke={line.base}
            strokeWidth={stroke}
            strokeDasharray='4 6'
            strokeLinecap='round'
            transform={`rotate(-90 ${size / 2} ${size / 2})`} />
        </svg>
        {centre ? <Centre>{centre}</Centre> : null}
      </Wrap>
    )
  }

  const r = (size - stroke) / 2
  const C = 2 * Math.PI * r
  const total = segments.reduce((s, x) => s + (x.weight || 1), 0) || 1

  let offset = 0
  const arcs = []

  segments.forEach((seg, i) => {
    const slice = ((seg.weight || 1) / total) * C
    const safePct = seg.pct === null || seg.pct === undefined ? 0 : Math.max(0, Math.min(100, seg.pct))
    const completeLen = slice * (safePct / 100)
    const colour = seg.colour || '#33becc'

    arcs.push(
      <circle key={`bg-${i}`} cx={size / 2} cy={size / 2} r={r}
        stroke={colour} strokeOpacity={0.18} strokeWidth={stroke} fill='none'
        strokeDasharray={`${Math.max(slice - 1.5, 0)} ${C}`}
        strokeDashoffset={-offset}
        transform={`rotate(-90 ${size / 2} ${size / 2})`} />,
    )
    arcs.push(
      <circle key={`fg-${i}`} cx={size / 2} cy={size / 2} r={r}
        stroke={colour} strokeWidth={stroke} fill='none'
        strokeLinecap='butt'
        strokeDasharray={`${completeLen} ${C}`}
        strokeDashoffset={-offset}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        style={{ transition: 'stroke-dasharray 500ms ease' }} />,
    )
    offset += slice
  })

  return (
    <Wrap style={{ width: size, height: size }}>
      <svg width={size} height={size}>
        {arcs}
      </svg>
      {centre ? <Centre>{centre}</Centre> : null}
    </Wrap>
  )
}

export default SegmentedDonut

const Wrap = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`

const Centre = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-family: 'FuturaPT-Light', system-ui, sans-serif;
  font-size: ${font.heading};
  line-height: 1;
  color: ${({ theme }) => theme.colors.haiti};
`
